import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { formatApiErrorDetail } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

function AuthShell({ title, subtitle, accent = "yellow", children }) {
  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="flex justify-center mb-6">
          <img src="/paragon-logo.jpeg" alt="Paragon" className="h-16 w-16 rounded-full" />
        </Link>
        <div className="bg-[#121216] border border-white/10 rounded-xl p-8">
          <p className={`font-mono uppercase tracking-widest text-xs ${accent === "green" ? "text-emerald-400" : "text-yellow-400"}`}>{subtitle}</p>
          <h1 className="font-heading font-black uppercase text-3xl text-white mt-1 mb-6">{title}</h1>
          {children}
        </div>
      </div>
    </div>
  );
}

export function Login({ gym = false }) {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      const dest = u.role === "admin" ? "/admin" : u.role === "gym_owner" ? "/gym" : location.state?.from || "/account";
      window.location.assign(dest);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title={gym ? "Gym Owner Login" : "Sign In"} subtitle={gym ? "Wholesale Portal" : "Team Store"} accent={gym ? "green" : "yellow"}>
      <form onSubmit={submit} className="space-y-4">
        <div><Label className="text-zinc-400">Email</Label><Input data-testid="login-email" value={email} onChange={(e) => setEmail(e.target.value)} className="bg-[#0A0A0C] border-white/15 mt-1" /></div>
        <div><Label className="text-zinc-400">Password</Label><Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="bg-[#0A0A0C] border-white/15 mt-1" /></div>
        <button data-testid="login-submit" disabled={loading} className={`w-full px-6 py-3.5 rounded-sm ${gym ? "btn-green" : "btn-primary"} disabled:opacity-50`}>
          {loading ? "Signing in…" : "Sign In"}
        </button>
      </form>
      <div className="flex justify-between mt-4 text-sm">
        <Link to="/forgot-password" className="text-zinc-400 hover:text-yellow-400">Forgot password?</Link>
        {!gym && <Link to="/register" className="text-zinc-400 hover:text-yellow-400">Create account</Link>}
      </div>
    </AuthShell>
  );
}

export function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form);
      toast.success("Welcome to Paragon!");
      window.location.assign("/account");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Create Account" subtitle="Join The Team">
      <form onSubmit={submit} className="space-y-4">
        <div><Label className="text-zinc-400">Full Name</Label><Input data-testid="register-name" value={form.name} onChange={set("name")} className="bg-[#0A0A0C] border-white/15 mt-1" /></div>
        <div><Label className="text-zinc-400">Email</Label><Input data-testid="register-email" value={form.email} onChange={set("email")} className="bg-[#0A0A0C] border-white/15 mt-1" /></div>
        <div><Label className="text-zinc-400">Phone</Label><Input data-testid="register-phone" value={form.phone} onChange={set("phone")} className="bg-[#0A0A0C] border-white/15 mt-1" /></div>
        <div><Label className="text-zinc-400">Password</Label><Input data-testid="register-password" type="password" value={form.password} onChange={set("password")} className="bg-[#0A0A0C] border-white/15 mt-1" /></div>
        <button data-testid="register-submit" disabled={loading} className="btn-primary w-full px-6 py-3.5 rounded-sm disabled:opacity-50">
          {loading ? "Creating…" : "Create Account"}
        </button>
      </form>
      <p className="mt-4 text-sm text-zinc-400 text-center">Already have an account? <Link to="/login" className="text-yellow-400">Sign in</Link></p>
    </AuthShell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { api } = await import("../../lib/api");
      await api.post("/auth/forgot-password", { email });
    } catch {}
    setSent(true);
  };
  return (
    <AuthShell title="Reset Password" subtitle="Account Recovery">
      {sent ? (
        <div className="text-zinc-300">
          <p>If that email is registered, a reset link has been sent. Check your inbox.</p>
          <Link to="/login" className="text-yellow-400 inline-block mt-4">← Back to sign in</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div><Label className="text-zinc-400">Email</Label><Input data-testid="forgot-email" value={email} onChange={(e) => setEmail(e.target.value)} className="bg-[#0A0A0C] border-white/15 mt-1" /></div>
          <button data-testid="forgot-submit" className="btn-primary w-full px-6 py-3.5 rounded-sm">Send Reset Link</button>
          <Link to="/login" className="text-zinc-400 hover:text-yellow-400 text-sm block text-center">← Back to sign in</Link>
        </form>
      )}
    </AuthShell>
  );
}

export function ResetPassword() {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const token = new URLSearchParams(window.location.search).get("token");
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { api } = await import("../../lib/api");
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated. Please sign in.");
      navigate("/login");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Reset failed");
    }
  };
  return (
    <AuthShell title="Set New Password" subtitle="Account Recovery">
      <form onSubmit={submit} className="space-y-4">
        <div><Label className="text-zinc-400">New Password</Label><Input data-testid="reset-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="bg-[#0A0A0C] border-white/15 mt-1" /></div>
        <button data-testid="reset-submit" className="btn-primary w-full px-6 py-3.5 rounded-sm">Update Password</button>
        <Link to="/login" className="text-zinc-400 hover:text-yellow-400 text-sm block text-center">← Back to sign in</Link>
      </form>
    </AuthShell>
  );
}
